// ============================================================================
// RESUME PARSER - Client-Side Only
// Extracts text from PDF and DOCX files in the browser
// ============================================================================

import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';

// Configure PDF.js worker - use unpkg CDN with ES module format
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

/**
 * Parse text from PDF file
 * @param {File} file - PDF file object
 * @returns {Promise<string>} - Extracted text
 */
export const parsePDF = async (file) => {
  try {
    console.log('Starting PDF parse for file:', file.name, 'Size:', file.size);
    const arrayBuffer = await file.arrayBuffer();
    console.log('ArrayBuffer loaded, size:', arrayBuffer.byteLength);

    // Load the PDF document
    const loadingTask = pdfjsLib.getDocument({
      data: arrayBuffer,
    });

    console.log('PDF loading task created');
    const pdf = await loadingTask.promise;
    console.log('PDF loaded successfully, pages:', pdf.numPages);

    let fullText = '';

    // Extract text from each page
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(' ');
      fullText += pageText + '\n';
      console.log(`Page ${i} extracted, chars: ${pageText.length}`);
    }

    console.log('Total text extracted:', fullText.length, 'characters');

    if (!fullText.trim()) {
      throw new Error('No text content found in PDF. The PDF might be scanned or image-based.');
    }

    return fullText;
  } catch (error) {
    console.error('PDF parsing error details:', {
      message: error.message,
      name: error.name,
      stack: error.stack
    });

    if (error.message.includes('No text content')) {
      throw error;
    }

    // Provide more specific error messages
    if (error.message.includes('worker')) {
      throw new Error('PDF worker failed to load. Please check your internet connection and try again.');
    }

    throw new Error(`Failed to parse PDF: ${error.message}`);
  }
};

/**
 * Parse text from DOCX file
 * @param {File} file - DOCX file object
 * @returns {Promise<string>} - Extracted text
 */
export const parseDOCX = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
  } catch (error) {
    console.error('DOCX parsing error:', error);
    throw new Error('Failed to parse DOCX file. Please ensure it is a valid DOCX.');
  }
};

/**
 * Parse resume from file or text
 * @param {Object} input - { file: File } or { text: string }
 * @returns {Promise<string>} - Extracted text
 */
export const parseResume = async (input) => {
  if (input.text) {
    return input.text;
  }

  if (input.file) {
    const file = input.file;
    const fileType = file.type;
    const fileName = file.name.toLowerCase();

    if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
      return await parsePDF(file);
    } else if (
      fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      fileName.endsWith('.docx')
    ) {
      return await parseDOCX(file);
    } else {
      throw new Error('Unsupported file type. Please upload a PDF or DOCX file.');
    }
  }

  throw new Error('No valid input provided. Please provide a file or text.');
};

/**
 * Normalize text for processing
 * @param {string} text - Raw text
 * @returns {string} - Normalized text
 */
export const normalizeText = (text) => {
  if (!text) return '';

  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ') // Replace punctuation with spaces
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
};

/**
 * Detect sections in resume text
 * @param {string} text - Resume text
 * @returns {Object} - Detected sections
 */
export const detectSections = (text) => {
  const lowerText = text.toLowerCase();

  const sections = {
    hasExperience: false,
    hasEducation: false,
    hasSkills: false,
    hasProjects: false,
    hasCertifications: false,
    hasSummary: false
  };

  // Experience detection
  const experienceKeywords = ['experience', 'work history', 'employment', 'professional experience'];
  sections.hasExperience = experienceKeywords.some(keyword => lowerText.includes(keyword));

  // Education detection
  const educationKeywords = ['education', 'academic', 'degree', 'university', 'college'];
  sections.hasEducation = educationKeywords.some(keyword => lowerText.includes(keyword));

  // Skills detection
  const skillsKeywords = ['skills', 'technical skills', 'competencies', 'expertise'];
  sections.hasSkills = skillsKeywords.some(keyword => lowerText.includes(keyword));

  // Projects detection
  const projectsKeywords = ['projects', 'portfolio', 'personal projects'];
  sections.hasProjects = projectsKeywords.some(keyword => lowerText.includes(keyword));

  // Certifications detection
  const certificationsKeywords = ['certifications', 'certificates', 'licenses', 'credentials'];
  sections.hasCertifications = certificationsKeywords.some(keyword => lowerText.includes(keyword));

  // Summary detection
  const summaryKeywords = ['summary', 'objective', 'profile', 'about'];
  sections.hasSummary = summaryKeywords.some(keyword => lowerText.includes(keyword));

  return sections;
};

/**
 * Detect formatting issues
 * @param {string} text - Resume text
 * @returns {Object} - Formatting issues
 */
export const detectFormattingIssues = (text) => {
  const issues = {
    hasTables: false,
    hasIcons: false,
    hasImages: false,
    hasComplexLayout: false,
    issues: []
  };

  // Check for table indicators
  const tableIndicators = ['│', '┌', '┐', '└', '┘', '├', '┤', '┬', '┴', '┼'];
  issues.hasTables = tableIndicators.some(indicator => text.includes(indicator));
  if (issues.hasTables) {
    issues.issues.push('Tables detected - may not parse well in ATS');
  }

  // Check for icons/emojis
  const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u;
  issues.hasIcons = emojiRegex.test(text) || text.includes('●') || text.includes('■') || text.includes('▪');
  if (issues.hasIcons) {
    issues.issues.push('Icons or special characters detected');
  }

  // Check for excessive whitespace (complex layout indicator)
  const lines = text.split('\n');
  const emptyLineRatio = lines.filter(line => line.trim() === '').length / lines.length;
  issues.hasComplexLayout = emptyLineRatio > 0.4;
  if (issues.hasComplexLayout) {
    issues.issues.push('Complex layout detected - may indicate multi-column format');
  }

  return issues;
};

/**
 * Estimate page count from text
 * @param {string} text - Resume text
 * @returns {number} - Estimated page count
 */
export const estimatePageCount = (text) => {
  const wordCount = text.split(/\s+/).length;
  // Assuming ~500 words per page
  return Math.ceil(wordCount / 500);
};

/**
 * Check for bullet points usage
 * @param {string} text - Resume text
 * @returns {boolean} - Has bullet points
 */
export const hasBulletPoints = (text) => {
  const bulletIndicators = ['•', '·', '-', '*', '◦'];
  return bulletIndicators.some(bullet => text.includes(bullet));
};

/**
 * Check for action verbs
 * @param {string} text - Resume text
 * @returns {number} - Count of action verbs found
 */
export const countActionVerbs = (text) => {
  const actionVerbs = [
    'achieved', 'developed', 'implemented', 'created', 'designed', 'built',
    'managed', 'led', 'improved', 'increased', 'reduced', 'optimized',
    'delivered', 'launched', 'coordinated', 'executed', 'established',
    'streamlined', 'spearheaded', 'orchestrated', 'facilitated', 'analyzed'
  ];

  const lowerText = text.toLowerCase();
  return actionVerbs.filter(verb => lowerText.includes(verb)).length;
};
